// src/pages/financeiro/FinanceiroContratoFormPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";

import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import LayoutDesktop from "../../layouts/LayoutDesktop";
import { db } from "../../config/firebase";
import { sincronizarPlanejamentoContratoAprovado } from "../../services/abrirPreventivasClienteApi";
import { apiEnviarPropostaEmail } from "../../services/financeiroPropostasEmailApi";
import { blobToBase64, gerarPdfContrato } from "../../services/financeiroPropostasPdfService";

const COLLECTION_CONTRATOS = "financeiro_contratos";

const STATUS_OPTIONS = [
  { value: "CRIADO", label: "Criado" },
  { value: "ENVIADO", label: "Enviado" },
  { value: "APROVADO", label: "Aprovado" },
  { value: "REPROVADO", label: "Reprovado" },
];

function toInputDate(value) {
  if (!value) return "";
  const d = value?.toDate ? value.toDate() : (value instanceof Date ? value : new Date(value));
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export default function FinanceiroContratoFormPage() {
  const { tema } = useTheme();
  const { usuario } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();

  const [clientes, setClientes] = useState([]);
  const [academias, setAcademias] = useState([]);

  const [clienteId, setClienteId] = useState("");
  const [academiaId, setAcademiaId] = useState("");
  const [academiasSelecionadas, setAcademiasSelecionadas] = useState([]);

  const [contratoTipo, setContratoTipo] = useState("Mensal");
  const [status, setStatus] = useState("CRIADO");
  const [duracao, setDuracao] = useState("");
  const [dataInicial, setDataInicial] = useState("");
  const [visitasEmergenciais, setVisitasEmergenciais] = useState("");

  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const [carregando, setCarregando] = useState(false);
  const [carregandoClientes, setCarregandoClientes] = useState(false);
  const [carregandoAcademias, setCarregandoAcademias] = useState(false);
  const [carregandoContrato, setCarregandoContrato] = useState(false);
  const [gerandoPdf, setGerandoPdf] = useState(false);
  const [enviandoEmail, setEnviandoEmail] = useState(false);

  const isEdicao = !!id;

  useEffect(() => {
    const carregarClientes = async () => {
      if (!usuario?.grupoId) return;
      setCarregandoClientes(true);
      try {
        const snap = await getDocs(
          query(collection(db, "empresas"), where("grupoId", "==", usuario.grupoId))
        );
        const lista = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR"));
        setClientes(lista);
      } catch (e) {
        console.error("Erro ao carregar clientes:", e);
      } finally {
        setCarregandoClientes(false);
      }
    };

    carregarClientes();
  }, [usuario?.grupoId]);

  useEffect(() => {
    const carregarContrato = async () => {
      if (!isEdicao || !usuario?.grupoId) return;
      setCarregandoContrato(true);
      setErro("");
      try {
        const ref = doc(db, COLLECTION_CONTRATOS, id);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          setErro("Contrato nao encontrado.");
          return;
        }
        const data = snap.data() || {};
        setClienteId(data.clienteId || "");
        setContratoTipo(data.contratoTipo || "Mensal");
        setStatus(data.status || "CRIADO");
        setDuracao(data.duracaoMeses != null ? String(data.duracaoMeses) : "");
        setDataInicial(toInputDate(data.dataInicial));
        setVisitasEmergenciais(
          data.visitasEmergenciais != null ? String(data.visitasEmergenciais) : ""
        );
        setAcademiasSelecionadas((data.academias || []).map((a) => ({
          id: a.id,
          nome: a.nome || a.codigo || "",
        })));
      } catch (e) {
        console.error("Erro ao carregar contrato:", e);
        setErro("Erro ao carregar contrato.");
      } finally {
        setCarregandoContrato(false);
      }
    };

    carregarContrato();
  }, [id, isEdicao, usuario?.grupoId]);

  useEffect(() => {
    const carregarAcademias = async () => {
      if (!usuario?.grupoId || !clienteId) {
        setAcademias([]);
        setAcademiaId("");
        return;
      }
      setCarregandoAcademias(true);
      try {
        const snap = await getDocs(
          query(
            collection(db, "setores"),
            where("grupoId", "==", usuario.grupoId),
            where("empresaId", "==", clienteId)
          )
        );
        const lista = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR"));
        setAcademias(lista);
        setAcademiaId("");
      } catch (e) {
        console.error("Erro ao carregar academias:", e);
        setAcademias([]);
      } finally {
        setCarregandoAcademias(false);
      }
    };

    carregarAcademias();
  }, [usuario?.grupoId, clienteId]);

  const academiasDisponiveis = useMemo(() => {
    const selecionadas = new Set(academiasSelecionadas.map((a) => String(a.id)));
    return academias.filter((a) => !selecionadas.has(String(a.id)));
  }, [academias, academiasSelecionadas]);

  const handleAdicionarAcademia = () => {
    if (!academiaId) return;
    const academia = academias.find((a) => String(a.id) === String(academiaId));
    if (!academia) return;
    setAcademiasSelecionadas((prev) => [...prev, academia]);
    setAcademiaId("");
  };

  const handleRemoverAcademia = (idAcademia) => {
    setAcademiasSelecionadas((prev) => prev.filter((a) => String(a.id) !== String(idAcademia)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro("");
    setSucesso("");

    if (!usuario?.grupoId) {
      setErro("Usuario sem grupoId. Faca login novamente.");
      return;
    }

    if (!clienteId) {
      setErro("Selecione o cliente.");
      return;
    }

    if (!academiasSelecionadas.length) {
      setErro("Selecione ao menos uma academia.");
      return;
    }

    if (!contratoTipo) {
      setErro("Selecione o tipo de contrato.");
      return;
    }

    if (!dataInicial) {
      setErro("Informe a data inicial.");
      return;
    }

    setCarregando(true);
    try {
      const cliente = clientes.find((c) => String(c.id) === String(clienteId));
      const duracaoNum = duracao ? Number(duracao) : null;
      const visitasNum = visitasEmergenciais ? Number(visitasEmergenciais) : null;
      const aprovadoAgora = String(status || "").toUpperCase() === "APROVADO";
      const aprovadoEmRef = aprovadoAgora ? new Date() : null;
      let contratoIdSalvo = id || null;

      const payload = {
        grupoId: usuario.grupoId,
        empresaId: clienteId,
        clienteId,
        clienteNome: cliente?.nome || cliente?.razaoSocial || "",
        academias: academiasSelecionadas.map((a) => ({
          id: a.id,
          nome: a.nome || a.codigo || "",
        })),
        academiasIds: academiasSelecionadas.map((a) => a.id),
        contratoTipo,
        status,
        duracaoMeses: Number.isFinite(duracaoNum) ? duracaoNum : null,
        dataInicial: dataInicial ? new Date(`${dataInicial}T00:00:00`) : null,
        visitasEmergenciais: Number.isFinite(visitasNum) ? visitasNum : null,
        aprovadoEm: aprovadoAgora ? aprovadoEmRef : null,
        criadoPor: usuario?.uid || usuario?.id || "",
        updatedAt: serverTimestamp(),
      };

      if (isEdicao) {
        await updateDoc(doc(db, COLLECTION_CONTRATOS, id), payload);
        contratoIdSalvo = id;
        setSucesso("Contrato atualizado com sucesso.");
      } else {
        const novoRef = await addDoc(collection(db, COLLECTION_CONTRATOS), {
          ...payload,
          createdAt: serverTimestamp(),
        });
        contratoIdSalvo = novoRef.id;
        setSucesso("Contrato criado com sucesso.");
      }

      if (String(status || "").toUpperCase() === "APROVADO") {
        try {
          await sincronizarPlanejamentoContratoAprovado({
            grupoId: usuario?.grupoId,
            clienteId,
            clienteNome: cliente?.nome || cliente?.razaoSocial || "",
            empresaId: clienteId,
            contrato: {
              id: contratoIdSalvo,
              ...payload,
              status: "APROVADO",
            },
            criadoPor: usuario?.uid || usuario?.id || "",
            aprovadoEm: aprovadoEmRef || new Date(),
          });
        } catch (errSync) {
          console.warn("Falha ao sincronizar planejamento do contrato aprovado:", errSync);
        }
      }

      setTimeout(() => navigate("/financeiro/contratos"), 400);
    } catch (e2) {
      console.error("Erro ao salvar contrato:", e2);
      setErro(e2?.message || "Erro ao salvar contrato.");
    } finally {
      setCarregando(false);
    }
  };

  const handleGerarPdf = async () => {
    if (!usuario?.grupoId) {
      setErro("Usuário sem grupoId para gerar PDF.");
      return;
    }

    const cliente = clientes.find((c) => String(c.id) === String(clienteId));
    setErro("");
    setSucesso("");
    setGerandoPdf(true);
    try {
      const dadosPdf = {
        clienteNome: cliente?.nome || cliente?.razaoSocial || "",
        contratoTipo,
        status,
        duracaoMeses: duracao,
        dataInicial: dataInicial ? new Date(`${dataInicial}T00:00:00`) : null,
        visitasEmergenciais,
        academias: academiasSelecionadas,
      };

      await gerarPdfContrato({
        grupoId: usuario.grupoId,
        dados: dadosPdf,
      });
      setSucesso("PDF da proposta de contrato gerado com sucesso.");
    } catch (e) {
      setErro(String(e?.message || e));
    } finally {
      setGerandoPdf(false);
    }
  };

  const handleEnviarEmail = async () => {
    if (!usuario?.grupoId) {
      setErro("Usuário sem grupoId para enviar e-mail.");
      return;
    }

    const cliente = clientes.find((c) => String(c.id) === String(clienteId));
    const academiaPrincipal = academiasSelecionadas[0] || null;
    const academiaCompleta = academias.find((a) => String(a.id) === String(academiaPrincipal?.id)) || academiaPrincipal;
    const emailPadrao = String(
      academiaCompleta?.emailFinanceiro
        || academiaCompleta?.email
        || cliente?.emailFinanceiro
        || cliente?.email
        || ""
    ).trim();

    const toRaw = window.prompt("E-mail de destino", emailPadrao);
    if (toRaw == null) return;
    const to = String(toRaw || "").trim();
    if (!to) {
      setErro("Informe um e-mail de destino para enviar a proposta.");
      return;
    }

    const dadosPdf = {
      clienteNome: cliente?.nome || cliente?.razaoSocial || "",
      contratoTipo,
      status,
      duracaoMeses: duracao,
      dataInicial: dataInicial ? new Date(`${dataInicial}T00:00:00`) : null,
      visitasEmergenciais,
      academias: academiasSelecionadas,
    };

    setErro("");
    setSucesso("");
    setEnviandoEmail(true);
    try {
      const { blob, fileName } = await gerarPdfContrato({
        grupoId: usuario.grupoId,
        dados: dadosPdf,
        retornarBlob: true,
      });
      const pdfBase64 = await blobToBase64(blob);

      await apiEnviarPropostaEmail({
        grupoId: usuario.grupoId,
        tipo: "contrato",
        to,
        subject: `Proposta de Contrato - ${dadosPdf.clienteNome || "Cliente"}`,
        fileName,
        pdfBase64,
        context: {
          clienteNome: dadosPdf.clienteNome,
          academiaNome: academiaCompleta?.nome || academiaCompleta?.codigo || "",
          tipoDocumento: "Proposta de Contrato",
        },
      });

      setSucesso(`Proposta enviada por e-mail para ${to}.`);
    } catch (e) {
      setErro(String(e?.message || e));
    } finally {
      setEnviandoEmail(false);
    }
  };

  return (
    <LayoutDesktop
      tema={tema}
      titulo={isEdicao ? "Editar Contrato" : "Novo Contrato"}
      subtitulo="Financeiro / Cadastros / Contratos"
    >
      <section className="pc-card pc-form-card">
        <header className="pc-form-header">
          <h3>{isEdicao ? "Editar contrato" : "Cadastro de contratos"}</h3>
          <p>Informe os dados do contrato vinculado ao cliente e academias.</p>
        </header>

        {carregandoContrato ? (
          <p style={{ padding: 10 }}>Carregando contrato...</p>
        ) : (
          <form onSubmit={handleSubmit} className="pc-form">
            {erro && (
              <div
                style={{
                  background: "#7f1d1d",
                  color: "#fecaca",
                  padding: 8,
                  borderRadius: 8,
                  marginBottom: 12,
                  fontSize: 14,
                }}
              >
                {erro}
              </div>
            )}

            {sucesso && (
              <div
                style={{
                  background: "#064e3b",
                  color: "#d1fae5",
                  padding: 8,
                  borderRadius: 8,
                  marginBottom: 12,
                  fontSize: 14,
                }}
              >
                {sucesso}
              </div>
            )}

            <div className="pc-form-grid">
              <div className="pc-form-group pc-form-group-full">
                <label>Selecionar cliente</label>
                <select
                  value={clienteId}
                  onChange={(e) => setClienteId(e.target.value)}
                  className="pc-input"
                  disabled={carregandoClientes || isEdicao}
                >
                  <option value="">Selecione o cliente</option>
                  {clientes.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nome || c.razaoSocial || c.id}
                    </option>
                  ))}
                </select>
              </div>

              <div className="pc-form-group pc-form-group-full">
                <label>Selecionar academias</label>
                <div style={{ display: "grid", gap: 8 }}>
                  {academiasSelecionadas.length === 0 ? (
                    <div style={{ fontSize: 13, opacity: 0.75 }}>
                      Nenhuma academia adicionada.
                    </div>
                  ) : (
                    academiasSelecionadas.map((a) => (
                      <div
                        key={a.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 8,
                          padding: "8px 10px",
                          borderRadius: 8,
                          border: "1px solid rgba(148,163,184,0.2)",
                          background: "rgba(15,23,42,0.35)",
                        }}
                      >
                        <span>{a.nome || a.codigo || a.id}</span>
                        <button
                          type="button"
                          className="pc-btn-secundario"
                          onClick={() => handleRemoverAcademia(a.id)}
                        >
                          Remover
                        </button>
                      </div>
                    ))
                  )}

                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <select
                      value={academiaId}
                      onChange={(e) => setAcademiaId(e.target.value)}
                      className="pc-input"
                      disabled={carregandoAcademias || academiasDisponiveis.length === 0}
                      style={{ minWidth: 220, flex: "1 1 220px" }}
                    >
                      <option value="">Selecione a academia</option>
                      {academiasDisponiveis.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.nome || a.codigo || a.id}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="pc-btn-secundario"
                      onClick={handleAdicionarAcademia}
                      disabled={!academiaId}
                    >
                      Adicionar academia
                    </button>
                  </div>
                </div>
              </div>

              <div className="pc-form-group">
                <label>Tipo de contrato</label>
                <select
                  value={contratoTipo}
                  onChange={(e) => setContratoTipo(e.target.value)}
                  className="pc-input"
                >
                  <option value="Quinzenal">Quinzenal</option>
                  <option value="Mensal">Mensal</option>
                  <option value="Trimestral">Trimestral</option>
                  <option value="Semestral">Semestral</option>
                </select>
              </div>

              <div className="pc-form-group">
                <label>Status do contrato</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="pc-input"
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="pc-form-group">
                <label>Duracao do contrato (meses)</label>
                <input
                  type="number"
                  min="1"
                  value={duracao}
                  onChange={(e) => setDuracao(e.target.value)}
                  className="pc-input"
                  placeholder="Ex: 12"
                />
              </div>

              <div className="pc-form-group">
                <label>Data inicial</label>
                <input
                  type="date"
                  value={dataInicial}
                  onChange={(e) => setDataInicial(e.target.value)}
                  className="pc-input"
                />
              </div>

              <div className="pc-form-group">
                <label>Quantidade de visitas emergenciais</label>
                <input
                  type="number"
                  min="0"
                  value={visitasEmergenciais}
                  onChange={(e) => setVisitasEmergenciais(e.target.value)}
                  className="pc-input"
                  placeholder="Ex: 2"
                />
              </div>
            </div>

            <div className="pc-form-actions">
              <button
                type="button"
                className="pc-btn-secundario"
                onClick={handleGerarPdf}
                disabled={carregando || gerandoPdf || enviandoEmail}
              >
                {gerandoPdf ? "Gerando PDF..." : "Gerar PDF"}
              </button>
              <button
                type="button"
                className="pc-btn-secundario"
                onClick={handleEnviarEmail}
                disabled={carregando || gerandoPdf || enviandoEmail}
              >
                {enviandoEmail ? "Enviando e-mail..." : "Enviar por e-mail"}
              </button>
              <button
                type="button"
                className="pc-btn-secundario"
                onClick={() => navigate("/financeiro/contratos")}
                disabled={carregando || enviandoEmail}
              >
                Voltar
              </button>
              <button type="submit" className="pc-btn-primario" disabled={carregando || enviandoEmail}>
                {carregando ? "Salvando..." : (isEdicao ? "Salvar alterações" : "Salvar contrato")}
              </button>
            </div>
          </form>
        )}
      </section>
    </LayoutDesktop>
  );
}
